import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await axios.post(`${API}/auth/register`, registerData);
      const { access_token, user } = response.data;
      
      login(user, access_token);
      toast.success("Inscription réussie !");
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="w-full max-w-md px-4">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#1A4D2E] hover:text-[#143d24] mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Retour à l'accueil</span>
        </Link>

        <div className="auth-card animate-slide-up">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Leaf className="h-8 w-8 text-[#1A4D2E]" />
            <span className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MesSubventions
            </span>
          </div>

          <h1 className="text-2xl font-bold text-center text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Créer un compte
          </h1>
          <p className="text-center text-slate-500 mb-8">
            Rejoignez MesSubventions gratuitement
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-slate-700">Prénom</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  placeholder="Jean"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="h-11 border-slate-200"
                  data-testid="register-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-slate-700">Nom</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  placeholder="Dupont"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="h-11 border-slate-200"
                  data-testid="register-lastname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="votre@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-11 border-slate-200"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-11 border-slate-200 pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="h-11 border-slate-200"
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1A4D2E] hover:bg-[#143d24] text-white font-medium rounded-lg btn-shine"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                  Inscription...
                </span>
              ) : (
                "S'inscrire"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-500">
              Déjà un compte ?{" "}
              <Link 
                to="/login" 
                className="text-[#1A4D2E] font-medium hover:underline"
                data-testid="login-link"
              >
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
